/**
 * Modo Ajuda do Admin — guia funcional completo da plataforma.
 *
 * Página estática (sem chamadas de API) com sidebar de tópicos, busca global
 * (filtra por título + conteúdo), e deep-link via hash (`#candidaturas`,
 * `#lgpd`, etc.) — o admin pode compartilhar links diretos entre a equipe.
 *
 * Cobertura: 11 tópicos operacionais (Candidaturas, Consultores, Pros,
 * Clínicas, Casos, Financeiro, Filho Presente, LGPD, Integrações, KPIs,
 * Troubleshooting). Conteúdo é 100% funcional — o que cada botão/aba faz,
 * quando usar, regras de negócio. Zero detalhe técnico (API/DB/schema).
 */
import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search, ChevronRight, ArrowLeft, HelpCircle, ClipboardCheck,
  UserCog, ShieldCheck, Building2, HeartHandshake, DollarSign,
  Radio, BarChart3, Puzzle, AlertTriangle, BookOpen, Users,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Definição do conteúdo — cada seção é uma const separada pra manutenção     */
/* fácil (adicionar seção nova = criar const + push em SECTIONS).             */
/* -------------------------------------------------------------------------- */

const SECTIONS = [
  {
    id: "visao-geral",
    icon: BookOpen,
    color: "text-slate-600",
    title: "Visão Geral",
    summary: "O que é a plataforma, como o RAF Direct e o Filho Presente conversam entre si.",
    blocks: [
      {
        h: "Missão da plataforma",
        p: [
          "RAF Direct: acolhimento inteligente + direcionamento seguro para famílias em busca de tratamento de saúde mental e dependência química. Do primeiro contato até a admissão em unidade parceira.",
          "Filho Presente (by RAF Direct): coordenação de cuidados de idosos. Ajuda famílias a organizar consultas, exames, fisioterapia, transporte, acompanhamento, ILPIs, casas de repouso, home care.",
        ],
      },
      {
        h: "Como as duas marcas conversam",
        p: [
          "Uma única base de dados canônica (`users`, `clinics`, `cases`). Profissionais e clínicas podem servir a RAF, o Filho Presente, ou ambos — controlado pelas flags `serves_raf` e `serves_fp`.",
          "Cadastros de clínica e profissional pelo Filho Presente (`/filho-presente/cadastre-*`) usam exatamente o mesmo formulário e o mesmo fluxo admin do RAF Direct (`/seja-parceiro` / `/seja-profissional`), diferindo apenas pelo branding âmbar e pelo trial Prata 3m automático concedido no aprovar.",
          "Vitrines públicas são anonimizadas: família só vê código (P001, P038), tier e cidade — nome, telefone e endereço são revelados exclusivamente pelo consultor durante o acolhimento.",
        ],
      },
      {
        h: "Papéis (roles) no sistema",
        list: [
          "**Admin**: acesso total. Aprova candidaturas, gerencia usuários, financeiro, LGPD, configurações.",
          "**Consultor RAF**: primeiro contato com a família, faz triagem, direciona para unidade. Pode entrar em plantão.",
          "**Profissional**: atende famílias via videoconsulta. Precisa de selo (Ouro/Prata/Bronze) para aparecer na vitrine.",
          "**Clínica**: unidade parceira que recebe encaminhamentos. Precisa aceitar termos, ter selo e capacidade.",
          "**Família**: usuário final que pede ajuda pela Central RAF ou Filho Presente.",
        ],
      },
    ],
  },
  {
    id: "candidaturas",
    icon: ClipboardCheck,
    color: "text-blue-600",
    title: "Candidaturas",
    summary: "Como aprovar consultores, profissionais e clínicas que se cadastram publicamente.",
    blocks: [
      {
        h: "Onde ver e por quê",
        p: [
          "Painel Admin → aba **Candidaturas**. Toda pessoa que preenche `/seja-consultor`, `/seja-profissional`, `/seja-parceiro`, `/filho-presente/cadastre-profissional` ou `/filho-presente/cadastre-clinica` cai aqui como `status=pending`.",
          "A candidatura NÃO cria conta — só depois do seu aprovar. Isso protege a plataforma de contas fantasmas e evita gasto de créditos com quem não vai atender.",
        ],
      },
      {
        h: "Como aprovar",
        list: [
          "**Clique em Aprovar** na linha da candidatura.",
          "O sistema gera uma senha temporária automaticamente + envia e-mail com passo-a-passo + envia WhatsApp (se Twilio estiver configurado + número válido).",
          "Se origem = Filho Presente, o profissional/clínica ganha **trial Prata de 3 meses** automaticamente pra aparecer na vitrine desde o dia 1.",
          "Clínicas FP-origem nascem com `serves_fp=True` E `serves_raf=True` (cross-list bidirecional) — o admin pode desligar depois via toggles.",
        ],
      },
      {
        h: "Como rejeitar",
        p: [
          "Clique em **Rejeitar** e escreva o motivo (fica no log de auditoria). O candidato NÃO recebe e-mail automático — se você quiser dar feedback, use o e-mail dele manualmente.",
        ],
      },
      {
        h: "FAQ",
        list: [
          "**E se o candidato usar um e-mail já cadastrado?** O sistema bloqueia no envio da candidatura (mensagem: 'Já existe conta com este e-mail').",
          "**Posso re-enviar a senha temporária?** Não pelo painel — precisa pedir ao candidato pra usar 'Esqueci minha senha'.",
          "**Consegui aprovar por engano — como reverter?** Vá em **Usuários**, encontre a conta criada e delete. A candidatura fica registrada como aprovada (histórico).",
        ],
      },
    ],
  },
  {
    id: "consultores",
    icon: Radio,
    color: "text-red-600",
    title: "Consultores & Plantão",
    summary: "Escalar consultores no plantão, gerenciar disponibilidade, ver quem está de plantão ao vivo.",
    blocks: [
      {
        h: "Plantão ao vivo",
        p: [
          "Botão vermelho no header do Admin: **Plantão ao vivo**. Mostra quem está online agora + tempo de plantão + últimos casos atendidos. Polling a cada 30 segundos.",
        ],
      },
      {
        h: "Escalar plantão semanal",
        p: [
          "Menu **Escala** (`/admin/escala`). Define quem cobre cada faixa horária da semana. Se ninguém está agendado, cai no fallback pra qualquer consultor logado.",
        ],
      },
      {
        h: "Ações rápidas no plantão",
        list: [
          "**Tirar do plantão**: força saída do consultor. Sistema tenta escalar automaticamente outro colega agendado. Se ninguém coberto, alerta amarelo.",
          "**Sala de Guerra** (botão preto): dashboard de guerra com todos os casos abertos em tempo real, alerts vermelhos pra urgências.",
        ],
      },
      {
        h: "FAQ",
        list: [
          "**Consultor não entra no plantão**: cheque se o email dele bate com um usuário `role=consultor` e status ativo. Force reload da página.",
          "**2 consultores atendendo o mesmo caso**: só quem clicou primeiro fica dono. O outro vê 'Caso já atribuído'.",
        ],
      },
    ],
  },
  {
    id: "profissionais",
    icon: UserCog,
    color: "text-emerald-600",
    title: "Profissionais & Certificação",
    summary: "Gerenciar credenciamento, tiers (Ouro/Prata/Bronze), vitalícios e trials.",
    blocks: [
      {
        h: "Tabela de Profissionais",
        p: [
          "Aba **Profissionais** no admin. Lista unificada RAF + Filho Presente. Cada linha mostra: nome, categoria, cidade, tier, status, e badges (RAF/FP).",
        ],
      },
      {
        h: "Tiers de certificação (regra de negócio)",
        list: [
          "**Ouro**: mensalidade R$149/mês. Aparece primeiro na vitrine, cards destacados, acesso a features premium.",
          "**Prata**: R$79/mês. Aparece no meio da vitrine.",
          "**Bronze**: R$29/mês. Aparece no final da vitrine.",
          "**Sem selo**: NÃO aparece na vitrine pública. Fica invisível pras famílias.",
        ],
      },
      {
        h: "Casos especiais (não cobram mensalidade)",
        list: [
          "**Vitalício**: acesso permanente ao Ouro sem cobrança. Usado pra parceiros estratégicos, sócios, embaixadores.",
          "**Trial**: 3 meses grátis (default Prata). Concedido automaticamente na aprovação FP. Vira Prata pago automaticamente ao expirar (se não pagar, cai pra invisível).",
          "**Cortesia**: você concede tier X por N meses grátis. Usa a aba **Cortesias** no admin.",
        ],
      },
      {
        h: "Como conceder cortesia",
        list: [
          "Vá em **Cortesias** (botão âmbar no header admin).",
          "Preencha: profissional, tier, meses, motivo.",
          "Salvar → o profissional vira Ouro/Prata/Bronze com `payment_status=waived` durante o período.",
          "Ao expirar, cai automaticamente para o tier anterior (ou fica sem selo).",
        ],
      },
      {
        h: "Migrar profissional legado",
        p: [
          "Se você tem um profissional na base antiga (`fp_professionals` sem `user_id`), o sistema já roda uma migração automática no startup ligando por e-mail. Se o profissional tem e-mail conflitante com outro role (admin, família), a migração faz **skip conflict** — você precisa resolver manualmente.",
        ],
      },
    ],
  },
  {
    id: "clinicas",
    icon: Building2,
    color: "text-amber-600",
    title: "Unidades / Clínicas",
    summary: "Gerenciar clínicas RAF e Filho Presente, tipos de estabelecimento, cross-listing.",
    blocks: [
      {
        h: "Aba Clínicas (Governança)",
        p: [
          "Lista unificada RAF + FP. Cada linha tem: nome, cidade, tipo de estabelecimento (ILPI, Casa de Repouso, Comunidade Terapêutica etc.), status, tier, público atendido, e toggles `serves_fp` e `serves_raf`.",
        ],
      },
      {
        h: "Cross-listing (aparecer nas duas vitrines)",
        list: [
          "**serves_raf**: se True, a clínica aparece em `/unidades` (vitrine RAF Direct).",
          "**serves_fp**: se True, a clínica aparece em `/filho-presente/clinicas`.",
          "Uma clínica pode aparecer em ambas — por exemplo, uma comunidade terapêutica que também atende idosos.",
          "Toggle direto na tabela ou dentro do painel `/admin/filho-presente` (aba Cross-list).",
        ],
      },
      {
        h: "Tipo de estabelecimento",
        p: [
          "Dropdown inline na coluna 'Tipo (vitrine)'. Escolhe entre: Clínica de Reabilitação (default RAF), Comunidade Terapêutica, Hospital Psiquiátrico, Consultório Psiquiátrico, ILPI, Casa de Repouso, Centro-Dia do Idoso, Home Care Geriátrico, Clínica de Fisioterapia Geriátrica, Clínica Médica, Laboratório, e mais.",
          "Vazio = 'Clínica de Reabilitação' como default (natureza majoritária da rede RAF).",
        ],
      },
      {
        h: "Vitrine anonimizada — REGRA CRÍTICA",
        p: [
          "Toda clínica aparece em vitrine pública SEM nome real. Só: código (P001), cidade/UF, tipo, público, tier e fotos. Nome real, telefone, endereço são SÓ revelados pelo consultor RAF durante o acolhimento com a família. Isso protege a clínica de contato spam e a família de direcionamento inadequado.",
          "Se você vir nome real vazando na vitrine, isso é um bug — reporte urgentemente.",
        ],
      },
      {
        h: "Auditoria: clínicas sem tipo",
        p: [
          "Aba **Auditoria** dentro de `/admin/filho-presente`. Lista clínicas com `establishment_type` vazio ou nulo. Banner verde quando zero. Banner âmbar + tabela quando existem — clique em 'Editar em Governança' pra corrigir.",
        ],
      },
    ],
  },
  {
    id: "casos",
    icon: HeartHandshake,
    color: "text-purple-600",
    title: "Casos & Consultas",
    summary: "Fluxo desde a família pedir ajuda até a consulta ser realizada.",
    blocks: [
      {
        h: "Fluxo de caso RAF Direct",
        list: [
          "**1. Família pede ajuda** em `/preciso-de-ajuda` — sistema faz triagem IA (GPT-5.2) e classifica urgência.",
          "**2. Caso vira `pending`** — cai no plantão dos consultores.",
          "**3. Consultor pega o caso** — clica em 'Atender' na Central de Atendimento.",
          "**4. Triagem humana** — consultor conversa com família (chat + WhatsApp) e recomenda unidade/profissional.",
          "**5. Consulta agendada** (opcional) — família paga via Stripe, agenda videoconsulta com profissional certificado.",
          "**6. Encaminhamento** — família recebe dados da unidade parceira e o processo de admissão.",
        ],
      },
      {
        h: "Central de Atendimento (admin)",
        p: [
          "`/admin/central-atendimento` — dashboard tempo real de casos abertos, com filtros por status/urgência/consultor. Chat interno + histórico de mensagens WhatsApp inbound/outbound.",
        ],
      },
      {
        h: "Sala de Guerra",
        p: [
          "`/admin/sala-de-guerra` — modo urgência: só casos críticos, cards vermelhos pulsantes, tempo desde último contato, alertas de SLA vencido.",
        ],
      },
      {
        h: "Casos reabertos / cancelados",
        p: [
          "Painéis no dashboard admin. Reabertos = família voltou pra pedir mais ajuda depois de fechar. Cancelados = família desistiu / consultor fechou sem admissão.",
        ],
      },
    ],
  },
  {
    id: "financeiro",
    icon: DollarSign,
    color: "text-green-600",
    title: "Financeiro",
    summary: "Repasses aos profissionais, cortesias, convênios, pagamentos Stripe.",
    blocks: [
      {
        h: "Repasse aos profissionais",
        p: [
          "Cada consulta paga gera um `repasse_previsto` para o profissional (valor da consulta menos comissão RAF). O admin envia via PIX no fim da semana.",
          "**`payout_paid_at`**: campo que marca quando o repasse foi efetivado. Se vazio, aparece na projeção do painel financeiro do profissional.",
          "Aba **Financeiro** no admin: filtros por profissional, período, status. Exporta CSV pra fazer PIX em lote.",
        ],
      },
      {
        h: "Cortesias",
        p: [
          "Aba âmbar no header. Concede tier grátis por período. Usa quando quer premiar/parceiro. Diferente de trial (que é automático 3m Prata) — cortesia é você quem escolhe tier + duração.",
        ],
      },
      {
        h: "Convênios",
        p: [
          "Aba **Convênios** — cadastra operadora (Bradesco, Amil, etc.), tabela de preço TISS, quais profissionais cobrem quais operadoras. Family reporter marca 'Aceita convênios' no card.",
          "**Estado atual**: infraestrutura pronta, aguardando contratos reais serem assinados antes de expor a página pública `/convenios`.",
        ],
      },
      {
        h: "Pagamentos Stripe",
        p: [
          "Assinaturas de tier (Ouro/Prata/Bronze) + consultas avulsas. Webhook processa `checkout.session.completed` e ativa selo. PIX status widget no dashboard mostra saúde da integração.",
          "**Cupons**: aba dedicada para criar cupons de desconto Stripe (percentual ou valor fixo).",
        ],
      },
    ],
  },
  {
    id: "filho-presente",
    icon: HeartHandshake,
    color: "text-amber-500",
    title: "Filho Presente",
    summary: "Painel específico do Filho Presente — beneficiários, planos, cross-list, dashboards FP.",
    blocks: [
      {
        h: "Onde",
        p: [
          "`/admin/filho-presente` — painel dedicado com abas: Dashboard, Aprovações, Beneficiários, Cross-list FP↔RAF, Auditoria.",
        ],
      },
      {
        h: "KPIs do Dashboard FP",
        list: [
          "**approved_professionals / approved_clinics**: quantos foram aprovados.",
          "**pending_professionals / pending_clinics**: aguardando sua análise.",
          "**cross_fp_to_raf**: clínicas nascidas no FP que também aparecem na RAF.",
          "**cross_raf_to_fp**: clínicas nascidas na RAF que também aparecem no FP.",
          "**open_requests / urgent_open**: solicitações de exame/transporte/acompanhante abertas.",
        ],
      },
      {
        h: "Aprovações FP",
        p: [
          "Ao aprovar um profissional/clínica FP, o sistema concede trial Prata 3m automaticamente e envia e-mail de boas-vindas com senha temporária. Cross-list toggle já vem ligado por default (a unidade nasce visível nas duas vitrines).",
        ],
      },
      {
        h: "Beneficiários",
        p: [
          "Pessoas cuidadas dentro do Filho Presente (o idoso da família). Cada beneficiário tem plano de cuidados, histórico médico, medicamentos, contatos de emergência.",
        ],
      },
      {
        h: "Planos Filho Presente",
        p: [
          "Aba **Planos** em `/filho-presente/planos`. Assinaturas mensais que dão acesso a features premium: assistente IA ilimitado, número maior de solicitações, coordenadora dedicada, etc. Stripe Subscriptions.",
        ],
      },
    ],
  },
  {
    id: "lgpd",
    icon: ShieldCheck,
    color: "text-emerald-700",
    title: "LGPD & Compliance",
    summary: "Solicitações de dados, consentimentos, DPO, política de privacidade.",
    blocks: [
      {
        h: "Onde",
        p: [
          "`/admin/lgpd` — painel de solicitações LGPD.",
        ],
      },
      {
        h: "Tipos de solicitação",
        list: [
          "**Acesso**: usuário pede pra ver quais dados temos dele.",
          "**Retificação**: usuário pede correção de dado errado.",
          "**Anonimização**: usuário pede pra apagar identidade mantendo estatísticas anônimas.",
          "**Exclusão**: usuário pede exclusão total (direito ao esquecimento).",
          "**Oposição**: usuário se opõe a certo processamento (ex.: marketing).",
        ],
      },
      {
        h: "Prazo de resposta (LGPD Art. 19)",
        p: [
          "Até 15 dias corridos. Painel destaca em vermelho as solicitações próximas do vencimento.",
        ],
      },
      {
        h: "DPO",
        p: [
          "Encarregado de Dados: **contato@rafdirect.com.br** (env `RAF_DPO_EMAIL`). Toda comunicação oficial LGPD deve passar pelo DPO.",
        ],
      },
      {
        h: "Versões de consentimento",
        list: [
          "**Caso (família)**: `2026-02-05.v1` — Art. 7 + Art. 11 + Termos.",
          "**Clínica**: `clinica-lgpd-v2-2026-02` — 6 checkboxes (LGPD, Confidencialidade, Termos, Divulgação, Critérios, Comissão).",
          "Se alterar termos, incremente a versão — usuários serão obrigados a re-aceitar no próximo login.",
        ],
      },
      {
        h: "Anonimização vs Exclusão",
        p: [
          "**Anonimização**: apagamos identidade (nome, e-mail, CPF, telefone) mas mantemos o `id` e as agregações estatísticas (número de casos, tipo de tratamento). Ideal quando precisa de rastreabilidade sem violar direito ao esquecimento.",
          "**Exclusão total**: remove tudo. Log de auditoria continua no `audit_logs` com actor_id genérico 'ANONYMIZED'.",
        ],
      },
    ],
  },
  {
    id: "integracoes",
    icon: Puzzle,
    color: "text-indigo-600",
    title: "Integrações",
    summary: "Stripe, Twilio, SendGrid, JaaS 8x8, OpenAI — como configurar e diagnosticar.",
    blocks: [
      {
        h: "Stripe (pagamentos + cupons)",
        list: [
          "Assinaturas mensais de tier + consultas avulsas.",
          "Chave viva em variável de ambiente. Webhooks entram em `/api/webhooks/stripe`.",
          "**PIX Status Widget** no dashboard admin mostra saúde da integração em tempo real.",
          "Se pagamento falha: veja logs no Stripe Dashboard e no widget.",
        ],
      },
      {
        h: "Twilio (WhatsApp + SMS)",
        list: [
          "Notificações automáticas de aprovação, lembretes de consulta, mensagens outbound.",
          "**Fora da janela 24h**: só envia se o número já iniciou conversa com nosso WA. Se falhar, sistema gera fallback wa.me link automático.",
          "**Live Notifier** no dashboard mostra mensagens inbound em tempo real (WebSocket).",
        ],
      },
      {
        h: "SendGrid (e-mails transacionais)",
        p: [
          "E-mails de boas-vindas, aprovação, reset de senha, notificação DPO. **Templates Health Panel** monitora bounce rate + delivery rate.",
        ],
      },
      {
        h: "JaaS 8x8 (videoconsulta)",
        p: [
          "Salas Jitsi hospedadas com token JWT assinado. Cada consulta gera sala única com nome baseado no booking_id. Gravação opcional.",
        ],
      },
      {
        h: "OpenAI GPT-5.2 (Emergent LLM Key)",
        list: [
          "**Triagem inteligente**: analisa mensagem inicial da família e classifica urgência + tipo de tratamento.",
          "**Sofia (assistente IA)**: chat de suporte pra famílias.",
          "**Assistente Filho Presente**: coordena cuidados de idoso, responde perguntas de saúde.",
        ],
      },
    ],
  },
  {
    id: "kpis",
    icon: BarChart3,
    color: "text-cyan-600",
    title: "KPIs & Dashboards",
    summary: "Métricas principais e como interpretá-las.",
    blocks: [
      {
        h: "Dashboard Admin — visão geral",
        list: [
          "**Total de usuários por role**: quantas famílias, consultores, profissionais, clínicas cadastrados.",
          "**Casos hoje/mês**: novos casos entrando + fechados.",
          "**Consultas realizadas**: videoconsultas concluídas no período.",
          "**Repasses pendentes**: total em R$ que precisa pagar aos profissionais.",
        ],
      },
      {
        h: "Cert Analytics",
        p: [
          "Aba dedicada. Mostra downloads de certificados, taxa de renovação de tier, upgrades/downgrades.",
        ],
      },
      {
        h: "Analytics Live",
        p: [
          "Aba **Ao vivo no site** (vermelha pulsante). Mostra visitas em tempo real, páginas mais quentes, fluxo de conversão (visitante → triagem → agendamento).",
        ],
      },
      {
        h: "Comms KPI",
        list: [
          "**Delivery rate WhatsApp**: % de mensagens entregues.",
          "**Response rate**: % de famílias que respondem em <15min.",
          "**Bounce rate e-mail**: % de e-mails que voltam.",
          "Todos aparecem no card `CommKpiCard` no topo do dashboard.",
        ],
      },
      {
        h: "Filho Presente Dashboard",
        p: [
          "Ver seção Filho Presente acima — 12 KPIs específicos (approved, pending, cross-list, urgent).",
        ],
      },
    ],
  },
  {
    id: "troubleshooting",
    icon: AlertTriangle,
    color: "text-orange-600",
    title: "Troubleshooting",
    summary: "Problemas comuns e como resolver rapidamente.",
    blocks: [
      {
        h: "Profissional aprovado não aparece na vitrine",
        list: [
          "1. Ele tem selo (tier)? Sem tier = invisível. Conceda tier ou trial.",
          "2. Se tem tier, `payment_status` está OK? Deve ser `paid`, `waived` ou `trial`. Se `pending` ou `cancelled`, não aparece.",
          "3. Certificação expirou? Cheque `certification_expires_at`. Se passou, renove.",
          "4. Vitalício sempre aparece — não precisa de expires_at.",
        ],
      },
      {
        h: "Clínica não aparece na vitrine",
        list: [
          "1. `is_public=True`? Se falso, admin desligou.",
          "2. Tem tier + payment_status válido? Mesma regra do profissional.",
          "3. `serves_raf=True` pra aparecer em /unidades. `serves_fp=True` pra aparecer em /filho-presente/clinicas.",
        ],
      },
      {
        h: "Família não recebe WhatsApp",
        list: [
          "1. Número tem 10 ou 11 dígitos + DDD? Sistema adiciona 55 automaticamente se necessário.",
          "2. Fora da janela 24h? Ela precisa iniciar conversa com nosso WA primeiro.",
          "3. Cheque Twilio Console: mensagem foi enviada? Fez bounce?",
          "4. Fallback wa.me link sempre é gerado — copie e mande manual.",
        ],
      },
      {
        h: "Consultor não consegue entrar em plantão",
        list: [
          "1. Cheque role no /admin/users — deve ser `consultor`.",
          "2. Está bloqueado? Aba **Banidos & Excluídos** no admin.",
          "3. Cache do navegador — peça pra ele fazer logout + login.",
        ],
      },
      {
        h: "Pagamento Stripe falhou",
        list: [
          "1. Cheque **PIX Status Widget** no dashboard.",
          "2. Vá no Stripe Dashboard → Payments → filtre por falha.",
          "3. Se webhook não processou, veja `stripe_webhook_events` no log.",
          "4. Reprocessar manualmente pelo Stripe Dashboard → 'Resend webhook'.",
        ],
      },
      {
        h: "E-mail não chegou pro candidato aprovado",
        list: [
          "1. Cheque **Templates Health Panel** — está com bounce alto?",
          "2. E-mail caiu no spam do candidato? Peça pra checar.",
          "3. SendGrid Console → Activity → filtre pelo e-mail. Confirma delivery.",
          "4. Fallback manual: copie a senha temporária do log de auditoria e mande você mesmo.",
        ],
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Componente                                                                 */
/* -------------------------------------------------------------------------- */

// Renderiza texto simples, transformando **texto** em <b> e quebras de linha.
const RichText = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return <b key={i} className="text-slate-900">{p.slice(2, -2)}</b>;
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
};

const AdminAjuda = () => {
  const location = useLocation();
  const initialId = (location.hash || "#visao-geral").replace("#", "") || "visao-geral";
  const [active, setActive] = useState(initialId);
  const [query, setQuery] = useState("");

  // Sincroniza hash da URL com a seção ativa (deep-link)
  useEffect(() => {
    if (location.hash.replace("#", "") !== active) {
      window.history.replaceState(null, "", `#${active}`);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active, location.hash]);

  // Busca global: filtra seções que contenham a query no título/summary/blocks
  const filteredSections = useMemo(() => {
    if (!query.trim()) return SECTIONS;
    const q = query.toLowerCase().trim();
    return SECTIONS.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      if (s.summary.toLowerCase().includes(q)) return true;
      return s.blocks.some((b) => {
        if (b.h.toLowerCase().includes(q)) return true;
        if (b.p && b.p.some((t) => t.toLowerCase().includes(q))) return true;
        if (b.list && b.list.some((t) => t.toLowerCase().includes(q))) return true;
        return false;
      });
    });
  }, [query]);

  const activeSection = useMemo(
    () => SECTIONS.find((s) => s.id === active) || SECTIONS[0],
    [active],
  );

  // Se busca não encontra a seção ativa, seleciona a primeira que casou
  useEffect(() => {
    if (query && filteredSections.length && !filteredSections.find((s) => s.id === active)) {
      setActive(filteredSections[0].id);
    }
  }, [query, filteredSections, active]);

  const ActiveIcon = activeSection.icon;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-ajuda-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin" data-testid="admin-ajuda-back"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0B2B62] mb-1">
            <ArrowLeft className="w-3 h-3" /> Voltar ao painel
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-emerald-600" /> Modo Ajuda
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Guia funcional completo da plataforma — o que cada botão faz, quando usar, regras de negócio, FAQs.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar (ex.: cortesia, LGPD, WhatsApp)…"
            data-testid="admin-ajuda-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-4 lg:self-start" data-testid="admin-ajuda-sidebar">
          <nav className="bg-white border border-slate-200 rounded-xl p-2 space-y-0.5">
            {filteredSections.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-500">
                Nenhum tópico encontrado para "<i>{query}</i>". <button
                  onClick={() => setQuery("")}
                  className="text-emerald-700 hover:underline"
                >Limpar busca</button>.
              </div>
            ) : filteredSections.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  data-testid={`admin-ajuda-topic-${s.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    isActive
                      ? "bg-[#0B2B62] text-white"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : s.color}`} />
                  <span className="flex-1 font-medium">{s.title}</span>
                  {isActive && <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              );
            })}
          </nav>
          <div className="mt-3 px-3 text-[11px] text-slate-400 leading-relaxed">
            Cada seção também é acessível via link direto: <code className="text-slate-600">/admin/ajuda#{activeSection.id}</code>
          </div>
        </aside>

        {/* Conteúdo */}
        <main className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8" data-testid="admin-ajuda-content">
          <div className="flex items-start gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50`}>
              <ActiveIcon className={`w-5 h-5 ${activeSection.color}`} />
            </div>
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0B2B62]" data-testid="admin-ajuda-title">
                {activeSection.title}
              </h2>
              <p className="text-sm text-slate-600 mt-1">{activeSection.summary}</p>
            </div>
          </div>

          <div className="space-y-8">
            {activeSection.blocks.map((b, idx) => (
              <section key={idx} data-testid={`admin-ajuda-block-${idx}`}>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-2">
                  {b.h}
                </h3>
                {b.p && (
                  <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
                    {b.p.map((t, i) => (
                      <p key={i}><RichText text={t} /></p>
                    ))}
                  </div>
                )}
                {b.list && (
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700 leading-relaxed list-disc pl-5">
                    {b.list.map((t, i) => (
                      <li key={i}><RichText text={t} /></li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3 text-xs text-slate-500">
            <div className="inline-flex items-center gap-2">
              <Users className="w-3.5 h-3.5" />
              Precisa de mais detalhes técnicos (endpoints, DB)? Fale com o dev de plantão.
            </div>
            <div className="text-slate-400">Última atualização: Iter 68.66.n · Feb 2026</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminAjuda;
